import { rebuildFromMoves } from './engine';
import type { CellIndex, GamePayload, GameState, Mark } from './types';

const VERSION_PREFIX = 'v1.';

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/u, '');
}

function fromBase64Url(value: string): Uint8Array {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const padLength = (4 - (padded.length % 4)) % 4;
  const base64 = padded + '='.repeat(padLength);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function encodeUtf8(text: string): Uint8Array {
  return new TextEncoder().encode(text);
}

function decodeUtf8(bytes: Uint8Array): string {
  return new TextDecoder().decode(bytes);
}

function isMark(value: unknown): value is Mark {
  return value === 'X' || value === 'O';
}

function isCellIndex(value: unknown): value is CellIndex {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 8;
}

function assertPayload(raw: unknown): GamePayload {
  if (!raw || typeof raw !== 'object') {
    throw new Error('invalid_payload');
  }
  const obj = raw as Record<string, unknown>;
  if (typeof obj.id !== 'string' || !obj.id.trim()) {
    throw new Error('invalid_id');
  }
  if (typeof obj.x !== 'string') {
    throw new Error('invalid_name_x');
  }
  if (typeof obj.o !== 'string') {
    throw new Error('invalid_name_o');
  }
  if (!isMark(obj.s)) {
    throw new Error('invalid_starter');
  }
  if (!Array.isArray(obj.m) || !obj.m.every(isCellIndex)) {
    throw new Error('invalid_moves');
  }
  return {
    id: obj.id.trim(),
    x: obj.x.trim(),
    o: obj.o.trim(),
    s: obj.s,
    m: obj.m,
  };
}

export function stateToPayload(state: GameState): GamePayload {
  return {
    id: state.id,
    x: state.nameX,
    o: state.nameO,
    s: state.starter,
    m: [...state.moves],
  };
}

export function encodeGame(state: GameState): string {
  const json = JSON.stringify(stateToPayload(state));
  return `${VERSION_PREFIX}${toBase64Url(encodeUtf8(json))}`;
}

export function decodeGame(hashCode: string): GameState {
  const code = hashCode.startsWith('#') ? hashCode.slice(1) : hashCode;
  if (!code.startsWith(VERSION_PREFIX)) {
    throw new Error('unsupported_version');
  }
  const encoded = code.slice(VERSION_PREFIX.length);
  if (!encoded) {
    throw new Error('empty_code');
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(decodeUtf8(fromBase64Url(encoded)));
  } catch {
    throw new Error('corrupt_code');
  }

  const payload = assertPayload(parsed);
  return rebuildFromMoves({
    id: payload.id,
    nameX: payload.x,
    nameO: payload.o,
    starter: payload.s,
    moves: payload.m,
  });
}

export function buildShareUrl(origin: string, state: GameState): string {
  const base = origin.endsWith('/') ? origin.slice(0, -1) : origin;
  return `${base}/#${encodeGame(state)}`;
}

export function readHashCode(hash: string): string | null {
  const value = hash.startsWith('#') ? hash.slice(1) : hash;
  return value.trim() ? value.trim() : null;
}
